import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import React, { useState } from "react";

const tierLegendStyles = {
  mt: 1,
} satisfies SxProps<Theme>;

const tierLegendHeaderStyles = {
  mb: 0.5,
} satisfies SxProps<Theme>;

/**
 * Plain-language tier definitions, tracking the DE scorer's exclusion sets
 * (`M1_EXCLUDED_FIELDS` / `M2_EXCLUDED_FIELDS` in `eval/scoring/scorer.ts`).
 * Kept here as copy so the dashboard explains the tiers without importing the
 * service's taxonomy.
 */
const ALL_TIER_DESCRIPTION =
  "Every scored field, with nothing excluded — the broadest, strictest view.";
const M1_TIER_DESCRIPTION =
  "Excludes the deadline-date fields, which are computed downstream and never extracted by the model — so scoring them would penalize the extractor for something it does not produce.";
const M2_TIER_DESCRIPTION =
  "M1's exclusions plus operator-authored SPECIAL_INSTRUCTIONS. The narrowest tier: the fields the extractor is primarily accountable for.";

const TIER_LEGEND: ReadonlyArray<{ label: string; description: string }> = [
  { label: "All fields", description: ALL_TIER_DESCRIPTION },
  { label: "M1 · addressable", description: M1_TIER_DESCRIPTION },
  { label: "M2 · primary", description: M2_TIER_DESCRIPTION },
];

/**
 * Renders the plain-language accuracy-tier legend: an "Accuracy tiers" heading
 * over one caption per tier, sourced from the copied DE scorer tier definitions
 * (`TIER_LEGEND`). Static copy — takes no props.
 */
const TierLegend = () => (
  <Box sx={tierLegendStyles}>
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={tierLegendHeaderStyles}
    >
      <InfoOutlinedIcon fontSize="inherit" color="disabled" />
      <Typography variant="caption" color="text.secondary">
        Accuracy tiers
      </Typography>
    </Stack>
    <Stack spacing={0.25}>
      {TIER_LEGEND.map((tier) => (
        <Typography key={tier.label} variant="caption" color="text.secondary">
          <strong>{tier.label}:</strong> {tier.description}
        </Typography>
      ))}
    </Stack>
  </Box>
);

/** A `{ pct, correct, total }` tally from the DE scorer. */
type FieldTally = {
  pct: number | null;
  correct: number;
  total: number;
};

type TierAggregate = {
  microEntity: number | null;
  microEntityFrac: [number, number];
  microStrict: number | null;
  microStrictFrac: [number, number];
  nDocs: number;
  perFamily: Record<string, FieldTally>;
  perField: Record<string, FieldTally>;
};

type RunAggregates = {
  nDocs: number;
  tiers: {
    all: TierAggregate;
    M1: TierAggregate;
    M2: TierAggregate;
  };
  shipperEntity: FieldTally;
};

type TierKey = "all" | "M1" | "M2";

const fmtPct = (pct: number | null, frac: [number, number]): string =>
  pct === null ? "—" : `${pct.toFixed(1)}% (${frac[0]}/${frac[1]})`;

const fmtTally = (t: FieldTally): string =>
  t.pct === null ? "—" : `${t.pct.toFixed(1)}% (${t.correct}/${t.total})`;

const TierCard = ({
  label,
  description,
  tier,
}: {
  readonly label: string;
  readonly description: string;
  readonly tier: TierAggregate;
}) => (
  <Card variant="outlined">
    <CardContent>
      <Tooltip title={description}>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
      </Tooltip>
      <Typography variant="body2">
        Entity:{" "}
        <strong>{fmtPct(tier.microEntity, tier.microEntityFrac)}</strong>
      </Typography>
      <Typography variant="body2">
        Strict: {fmtPct(tier.microStrict, tier.microStrictFrac)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {tier.nDocs} docs
      </Typography>
    </CardContent>
  </Card>
);

const TallyTable = ({
  title,
  tallies,
}: {
  readonly title: string;
  readonly tallies: Record<string, FieldTally>;
}) => {
  const rows = Object.entries(tallies).sort(([a], [b]) => a.localeCompare(b));
  return (
    <>
      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        {title}
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          None.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Key</TableCell>
              <TableCell align="right">Accuracy</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(([key, tally]) => (
              <TableRow key={key}>
                <TableCell>{key}</TableCell>
                <TableCell align="right">{fmtTally(tally)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
};

/**
 * Renders a run's scored aggregates: tier cards (all / M1 / M2 with dual
 * strict+entity accuracy), the shipper-entity headline, and per-family +
 * per-field accuracy tables for a selectable tier. Reads the opaque aggregates
 * JSON defensively — an in-flight run with no aggregates renders nothing.
 */
const AggregateTables = ({ aggregates }: { readonly aggregates: unknown }) => {
  const [tier, setTier] = useState<TierKey>("all");

  if (aggregates === null || aggregates === undefined) {
    return (
      <Typography variant="body2" color="text.secondary">
        Aggregates are available once the run completes.
      </Typography>
    );
  }

  const agg = aggregates as RunAggregates;
  const selected = agg.tiers[tier];

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <TierCard
            label="All fields"
            description={ALL_TIER_DESCRIPTION}
            tier={agg.tiers.all}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TierCard
            label="M1 (addressable)"
            description={M1_TIER_DESCRIPTION}
            tier={agg.tiers.M1}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TierCard
            label="M2 (primary)"
            description={M2_TIER_DESCRIPTION}
            tier={agg.tiers.M2}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent>
              <Tooltip title="Entity-token overlap over scoreable SHIPPER_NAME cells — the recorded shipper headline.">
                <Typography variant="overline" color="text.secondary">
                  Shipper entity
                </Typography>
              </Tooltip>
              <Typography variant="body2">
                <strong>{fmtTally(agg.shipperEntity)}</strong>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TierLegend />

      <ToggleButtonGroup
        exclusive
        size="small"
        value={tier}
        sx={{ mt: 2 }}
        onChange={(_e, next: TierKey | null) => {
          if (next !== null) setTier(next);
        }}
      >
        <ToggleButton value="all">All</ToggleButton>
        <ToggleButton value="M1">M1</ToggleButton>
        <ToggleButton value="M2">M2</ToggleButton>
      </ToggleButtonGroup>

      <TallyTable title="Per family" tallies={selected.perFamily} />
      <TallyTable title="Per field" tallies={selected.perField} />
    </>
  );
};

export default AggregateTables;
